import styles from './Header.module.css'
import { useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLanguage } from '@context/LanguageContext'
import { useDeviceType } from '@hooks/useDeviceType'
import { cx } from '@utils/cx'
import GameSelector from '../interface/selectors/GameSelector'
import SettingsModal from '../interface/modals/SettingsModal'
import MenuModal from '../interface/modals/MenuModal'
import LanguageSelector from '../interface/selectors/LanguageSelector'
import { MenuIcon, SettingsIcon, GamesIcon, SupportIcon, HomeIcon } from '../interface/Icons'

/**
 * Header component - Reusable navigation header
 *
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 */
export default function Header ({ className = '' }) {
  const [showMenuModal, setShowMenuModal] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [showGameSelector, setShowGameSelector] = useState(false)
  const gameSelectorTriggerRef = useRef(null)

  const { t, lang } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()

  // Device type detection
  const { isMobile, isTabletVertical, isTabletHorizontalOrDesktop } = useDeviceType()

  // Detect current route type
  const segments = location.pathname.split('/').filter(Boolean)
  const routeType = segments[1] // undefined for /:lang (home), 'memorama', 'puzzle', 'about', etc.

  // On home landing page, jersey is decorative and home button is hidden
  const isHomeLanding = !routeType

  // Navigation handlers
  const handleHomeClick = () => {
    navigate(`/${lang}`)
  }

  const handleSettingsClick = () => {
    setShowSettingsModal(true)
  }

  const handleMenuClick = () => {
    setShowMenuModal(true)
  }

  const handleCloseMenu = () => {
    setShowMenuModal(false)
  }

  const handleCloseSettings = () => {
    setShowSettingsModal(false)
  }

  const handleSupportClick = () => {
    navigate(`/${lang}/support`)
  }

  const handleGamesClick = () => {
    setShowGameSelector(prev => !prev)
  }

  const handleCloseGameSelector = () => {
    setShowGameSelector(false)
  }

  return (
    <>
      <header className={cx(styles.header, className)}>
        <div className={styles.logoContainer}>
          <h1 className='sr-only'>Memodiez</h1>
          {/*
              ✅ Decorative logo with fallback: alt provides visible text if image fails.
              Screen readers skip it (aria-hidden), but <h1> ensures semantic and accessible title.
              See WCAG: https://www.w3.org/WAI/tutorials/images/decorative/
          */}
          <img
            src='/assets/logos/logo.webp'
            srcSet='/assets/logos/logo-300.webp 300w, /assets/logos/logo-400.webp 400w, /assets/logos/logo-500.webp 500w'
            sizes='(max-width: 450px) min(60vw, 300px), (max-width: 900px) 400px, (max-width: 2560px) 500px, 500px'
            width='300'
            height='85'
            alt='Logo Memodiez'
            className={styles.logo}
            aria-hidden='true'
            decoding='async'
            loading='eager'
          />
        </div>

        {/* Navigation */}
        <nav aria-label={t.core.access.header.navigation} className={styles.nav}>
          {/* Mobile + Tablet Vertical: home button (only when NOT on home landing page) */}
          {(isMobile || isTabletVertical) && !isHomeLanding && (
            <button
              onClick={handleHomeClick}
              className={styles.navHome}
              aria-label={t.core.access.navigation.home}
              title={t.core.access.navigation.home}
            >
              <HomeIcon size={30} stroke={1.5} aria-hidden='true' />
              <span className='sr-only'>{t.core.access.navigation.home}</span>
            </button>
          )}

          {/* Desktop: Jersey element - decorative on home landing, home button on static pages */}
          {isTabletHorizontalOrDesktop && (
            isHomeLanding
              ? (
                <div
                  className={cx(styles.navJersey, styles.navJerseyInactive)}
                  role='img'
                  aria-label={t.core.header.iconAlt}
                >
                  <img
                    src='/assets/logos/memodiez-jersey.webp'
                    alt=''
                    className={styles.navJerseyImg}
                    aria-hidden='true'
                    decoding='async'
                    loading='eager'
                  />
                </div>
                )
              : (
                <button
                  onClick={handleHomeClick}
                  className={styles.navJersey}
                  aria-label={t.core.access.navigation.home}
                  title={t.core.access.navigation.home}
                >
                  <img
                    src='/assets/logos/memodiez-jersey.webp'
                    alt=''
                    className={styles.navJerseyImg}
                    aria-hidden='true'
                    decoding='async'
                    loading='eager'
                  />
                </button>
                )
          )}

          {/* Games icon - navigate to home/game selection */}
          <button
            ref={gameSelectorTriggerRef}
            onClick={handleGamesClick}
            className={styles.navGames}
            aria-label={t.core.access.navigation.games}
            title={t.core.access.navigation.games}
            aria-expanded={showGameSelector}
            aria-haspopup='true'
          >
            <GamesIcon size={30} stroke={1.5} aria-hidden='true' />
          </button>

          {/* Desktop: Expanded elements */}
          {isTabletHorizontalOrDesktop && (
            <>
              <button
                onClick={handleSupportClick}
                className={styles.navSupport}
                aria-label={t.core.access.navigation.support}
                title={t.core.access.navigation.support}
              >
                <SupportIcon size={30} stroke={1.5} aria-hidden='true' />
              </button>

              <div className={styles.navLanguage}>
                <LanguageSelector />
              </div>
            </>
          )}

          {/* Settings: always present */}
          <button
            onClick={handleSettingsClick}
            className={styles.navSettings}
            aria-label={t.core.access.navigation.settings}
            title={t.core.access.navigation.settings}
          >
            <SettingsIcon size={30} stroke={1.5} aria-hidden='true' />
          </button>

          {/* Mobile + Tablet Vertical: menu hamburger */}
          {(isMobile || isTabletVertical) && (
            <button
              onClick={handleMenuClick}
              className={styles.navMenu}
              aria-label={t.core.access.navigation.menu}
              title={t.core.access.navigation.menu}
            >
              <MenuIcon size={30} stroke={1.5} aria-hidden='true' />
            </button>
          )}
        </nav>
      </header>

      {/* Modal components */}
      {showSettingsModal && (
        <SettingsModal
          isOpen={showSettingsModal}
          onClose={handleCloseSettings}
        />
      )}

      <MenuModal
        isOpen={showMenuModal}
        onClose={handleCloseMenu}
      />

      <GameSelector
        isOpen={showGameSelector}
        onClose={handleCloseGameSelector}
        triggerRef={gameSelectorTriggerRef}
      />
    </>
  )
}
